import React, { useMemo, useState } from "react";
import { DENSITY_SIGNALS } from "../../../constants/activationConsoleData";

export default function ActivationClubsTab({
  state,
  openAddClub,
  updateClubStatus,
  toggleDensity,
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openDensity, setOpenDensity] = useState({});

  const counts = useMemo(() => {
    const clubs = state.clubs;
    return {
      anchor: clubs.filter((c) => c.status === "anchor").length,
      warm: clubs.filter((c) => c.status === "contacted" || c.status === "warm").length,
      pending: clubs.filter((c) => c.status === "pending").length,
    };
  }, [state.clubs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return state.clubs.filter((c) => {
      const matchSearch =
        !q || c.name.toLowerCase().includes(q) || (c.loc && c.loc.toLowerCase().includes(q));
      const matchFilter = filter === "all" || c.status === filter;
      return matchSearch && matchFilter;
    });
  }, [state.clubs, search, filter]);

  const toggleRow = (id) => {
    setOpenDensity((o) => ({ ...o, [id]: !o[id] }));
  };

  return (
    <>
      <div className="ac-panel" style={{ marginBottom: 18 }}>
        <div className="ac-panel-head">
          <div>
            <div className="ac-panel-title">Anchor Node Strategy</div>
            <div className="ac-panel-sub">
              Pick 3 and go deep. Phone-sharing density per node beats total activity across 50 clubs.
            </div>
          </div>
          <button type="button" className="ac-btn accent sm" onClick={openAddClub}>
            + Add Club
          </button>
        </div>
        <div className="ac-panel-body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              marginBottom: 0,
            }}
            className="ac-clubs-strategy-cols"
          >
            <div
              style={{
                background: "var(--green-bg)",
                border: "1px solid var(--green-bd)",
                borderRadius: "var(--r-lg)",
                padding: "16px 18px",
              }}
            >
              <div className="ac-section-label" style={{ color: "var(--green)", marginBottom: 8 }}>
                Anchor (3 target)
              </div>
              <div className="ac-serif" style={{ fontSize: 28, color: "var(--ink)" }}>
                {counts.anchor}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>Deep seeding underway</div>
            </div>
            <div
              style={{
                background: "var(--accent-bg)",
                border: "1px solid var(--accent-mid)",
                borderRadius: "var(--r-lg)",
                padding: "16px 18px",
              }}
            >
              <div className="ac-section-label" style={{ color: "var(--accent)", marginBottom: 8 }}>
                Warm (outreach sent)
              </div>
              <div className="ac-serif" style={{ fontSize: 28, color: "var(--ink)" }}>
                {counts.warm}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>Awaiting response</div>
            </div>
            <div
              style={{
                background: "var(--s3)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-lg)",
                padding: "16px 18px",
              }}
            >
              <div className="ac-section-label" style={{ marginBottom: 8 }}>Pending</div>
              <div className="ac-serif" style={{ fontSize: 28, color: "var(--ink)" }}>
                {counts.pending}
              </div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>Phase 2 targets</div>
            </div>
          </div>
          <div className="ac-inline-note" style={{ marginTop: 12 }}>
            <strong>Density &gt; reach.</strong> Click any club to see its signal density tracker.
          </div>
        </div>
      </div>

      <div className="ac-clubs-toolbar">
        <input
          placeholder="Search clubs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="warm">Warm</option>
          <option value="active">Active</option>
          <option value="anchor">Anchor</option>
        </select>
      </div>

      <div className="ac-clubs-list">
        {filtered.map((c, i) => {
          const density = state.clubDensity[c.id] || {};
          const sf = DENSITY_SIGNALS.filter((s) => density[s.id]).length;
          return (
            <div key={c.id} className="ac-club-row">
              <button type="button" className="ac-club-row-top" onClick={() => toggleRow(c.id)}>
                <div className="ac-cr-rank">{i + 1}</div>
                <div className={`ac-cr-priority priority-${c.priority}`} />
                <div className="ac-cr-info">
                  <div className="ac-cr-name">
                    {c.name}{" "}
                    {sf > 0 ? (
                      <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 400 }}>
                        · {sf} signal{sf > 1 ? "s" : ""} fired
                      </span>
                    ) : null}
                  </div>
                  <div className="ac-cr-meta">
                    <span>📍 {c.loc}</span>
                    <span>🏃 {c.runs}</span>
                    {c.notes ? <span style={{ color: "var(--dim)" }}>{c.notes}</span> : null}
                  </div>
                </div>
                <div
                  className="ac-cr-status"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <select
                    value={c.status}
                    onChange={(e) => updateClubStatus(c.id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="warm">Warm</option>
                    <option value="active">Active</option>
                    <option value="anchor">Anchor ★</option>
                  </select>
                </div>
                <span style={{ fontSize: 12, color: "var(--dim)", paddingLeft: 8 }}>▾</span>
              </button>
              <div className={`ac-club-density ${openDensity[c.id] ? "open" : ""}`}>
                <div className="ac-cd-title">Density Tracker — which signals have fired here</div>
                <div className="ac-cd-signals">
                  {DENSITY_SIGNALS.map((s) => (
                    <div key={s.id} className="ac-cd-signal">
                      <div className={`ac-cd-dot ${density[s.id] ? "yes" : "no"}`} />
                      <div className="ac-cd-label">{s.label}</div>
                      <button
                        type="button"
                        className={`ac-cd-btn ${density[s.id] ? "lit" : ""}`}
                        onClick={() => toggleDensity(c.id, s.id)}
                      >
                        {density[s.id] ? "✓ Done" : "Mark done"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
